import { Box, Button, Chip, CircularProgress, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { Refresh, Sync } from '@mui/icons-material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetROVVideoPendingCount, useGetROVVideoSyncProgress, useStartROVVideoSync } from 'src/api/rov-videos'
import GenericTable from 'src/components/table/GenericTable'
import { formatRemainingTime } from 'src/utils'

const STATUS_RUNNING = 'Running'

const outcomeColor = (outcome: string): 'success' | 'error' | 'warning' | 'default' => {
  if (outcome === 'Updated') return 'success'
  if (outcome === 'Failed') return 'error'
  if (outcome === 'Skipped') return 'warning'
  return 'default'
}

const BATCH_SIZE_OPTIONS = [50, 100, 200, 500]

const ROVVideoSyncDialog = () => {
  const { t } = useTranslation('data-sync')
  const [batchSize, setBatchSize] = useState(100)

  const { mutateAsync: startSync, isLoading: isStarting } = useStartROVVideoSync()
  const { data: progress, isFetching, refetch } = useGetROVVideoSyncProgress()
  const { data: pendingData, refetch: refetchPendingCount } = useGetROVVideoPendingCount()

  const isRunning = progress?.status === STATUS_RUNNING
  const pct =
    progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0

  const handleStartSync = async () => {
    await startSync({ batchSize }, {
      onSuccess: () => {
        toast.success(t('sync-started'))
        refetch()
      },
      onError: (error: any) => {
        if (error?.response?.status === 409) {
          toast.warning(t('sync-already-running'))
          refetch()
        } else {
          toast.error(t('sync-failed'))
        }
      },
    })
  }

  const tableRows = useMemo(() => {
    if (!progress) return []
    return progress.errors ?? []
  }, [progress?.errors])

  const columns: GridColumns = useMemo(
    () => [
      { field: 'videoId', headerName: t('rov-col-video-id'), width: 80 },
      { field: 'title', headerName: t('rov-col-title'), flex: 1, minWidth: 160 },
      { field: 'vimeoId', headerName: t('rov-col-vimeo-id'), flex: 1, minWidth: 160 },
      {
        field: 'outcome',
        headerName: t('rov-col-outcome'),
        width: 120,
        renderCell: ({ value }) => (
          <Chip label={value} color={outcomeColor(value)} size="small" />
        ),
      },
      { field: 'detail', headerName: t('rov-col-detail'), flex: 1, minWidth: 120 },
    ],
    [t],
  )

  return (
    <Box display="flex" flexDirection="column" gap={2} pb={1}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" paddingTop={1}>
        <TextField
          select
          size="small"
          label={t('batch-size')}
          value={batchSize}
          onChange={e => setBatchSize(Number(e.target.value))}
          sx={{ minWidth: 120 }}
          disabled={isStarting || isRunning}
        >
          {BATCH_SIZE_OPTIONS.map(size => (
            <MenuItem key={size} value={size}>{size}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          size="small"
          startIcon={<Sync />}
          onClick={handleStartSync}
          disabled={isStarting || isRunning}
        >
          {isStarting ? t('starting') : t('start-sync')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => {
            refetch()
            refetchPendingCount()
          }}
          disabled={isFetching}
        >
          {t('check-progress')}
        </Button>
        {isFetching && <CircularProgress size={18} />}
        {pendingData !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {t('pending-count')}: <strong>{pendingData.pendingCount}</strong>
          </Typography>
        )}
      </Stack>

      {progress && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">{t('progress')}</Typography>
            <Chip label={progress.status} size="small" />
          </Stack>

          <LinearProgress
            variant="determinate"
            value={pct}
          />

          <Stack direction="row" spacing={3} flexWrap="wrap">
            {[
              { label: t('total'), value: progress.total },
              { label: t('processed'), value: progress.processed },
              { label: t('updated'), value: progress.updated },
              { label: t('skipped'), value: progress.skipped },
              { label: t('failed'), value: progress.failed },
              ...(progress.total > 0 ? [{ label: t('percentage'), value: `${pct}%` }] : []),
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>

          {progress.startedAt && (
            <Typography variant="caption" color="text.secondary">
              {t('started-at')}: {new Date(progress.startedAt).toLocaleString()}
            </Typography>
          )}
          {progress.completedAt && (
            <Typography variant="caption" color="text.secondary">
              {t('completed-at')}: {new Date(progress.completedAt).toLocaleString()}
            </Typography>
          )}
          {progress.message && (
            <Typography variant="body2" color="text.secondary">
              {progress.message}
            </Typography>
          )}
          {progress.remainingSeconds !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t('remaining-time')}: {formatRemainingTime(progress.remainingSeconds)}
            </Typography>
          )}

          {tableRows.length > 0 && (
            <>
              <Typography variant="subtitle2">{t('rov-errors')}</Typography>
              <GenericTable
                id="rov-sync-results"
                data={tableRows}
                columns={columns}
                getRowId={row => row.videoId}
                enableToolbar={false}
                enablePagination={false}
                autoHeight={false}
                height={320}
                maxHeight={320}
                getRowHeight={() => 'auto'}
              />
            </>
          )}
        </Stack>
      )}

      {!progress && !isFetching && (
        <Typography variant="body2" color="text.secondary">
          {t('no-sync-run-yet')}
        </Typography>
      )}
    </Box>
  )
}

export default ROVVideoSyncDialog
